import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Gavel, Mail, Lock, User, Eye, EyeOff, Loader2, ShieldCheck, ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSlot,
} from "@/components/ui/input-otp";

// Login Page
export const LoginPage = () => {
    const { login, verify2FA, resend2FAOTP, googleLogin, isAuthenticated, error, setError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // 2FA state
    const [requires2FA, setRequires2FA] = useState(false);
    const [tempToken, setTempToken] = useState('');
    const [otpCode, setOtpCode] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        setError(null);
    }, [setError]);

    // Cooldown timer for resend
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const result = await login(email, password);
            
            if (result?.requires_2fa) {
                // 2FA required - show OTP input
                setRequires2FA(true);
                setTempToken(result.temp_token);
                setResendCooldown(60); // 60 seconds cooldown
                toast.info('Cod de verificare trimis pe email');
            } else if (result?.success) {
                toast.success('Autentificare reușită!');
                navigate('/');
            }
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FA = async (e) => {
        e?.preventDefault();
        if (otpCode.length !== 6) {
            toast.error('Introdu codul complet de 6 cifre');
            return;
        }
        
        setLoading(true);
        try {
            await verify2FA(email, otpCode, tempToken);
            toast.success('Autentificare reușită!');
            navigate('/');
        } catch (err) {
            toast.error(err.message);
            setOtpCode('');
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (resendCooldown > 0) return;
        
        try {
            await resend2FAOTP(tempToken);
            setResendCooldown(60);
            toast.success('Cod nou trimis pe email');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleBack = () => {
        setRequires2FA(false);
        setTempToken('');
        setOtpCode('');
        setPassword('');
    };

    // Google OAuth - redirect to backend which handles the OAuth flow
    const handleGoogleLogin = () => {
        const redirectUrl = window.location.pathname || '/';
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        window.location.href = `${backendUrl}/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // Facebook OAuth - uses Facebook SDK
    const [fbLoaded, setFbLoaded] = useState(false);
    const [fbLoading, setFbLoading] = useState(false);

    useEffect(() => {
        // Initialize Facebook SDK
        const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
        if (!facebookAppId) return;

        if (document.getElementById('facebook-jssdk')) {
            if (window.FB) setFbLoaded(true);
            return;
        }

        window.fbAsyncInit = function () {
            window.FB.init({
                appId: facebookAppId,
                xfbml: true,
                version: 'v18.0'
            });
            setFbLoaded(true);
        };

        const script = document.createElement('script');
        script.id = 'facebook-jssdk';
        script.src = 'https://connect.facebook.net/ro_RO/sdk.js';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
    }, []);

    const handleFacebookLogin = () => {
        if (!fbLoaded || typeof window.FB === 'undefined') {
            toast.error('Facebook SDK nu este încărcat');
            return;
        }

        setFbLoading(true);
        window.FB.login(
            (response) => {
                console.log('Facebook login response:', response);
                
                if (response.authResponse) {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                    fetch(`${backendUrl}/api/auth/facebook/login`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ accessToken: response.authResponse.accessToken })
                    })
                    .then(res => {
                        if (!res.ok) {
                            return res.json().then(err => {
                                throw new Error(err.detail || 'Autentificarea cu Facebook a eșuat');
                            });
                        }
                        return res.json();
                    })
                    .then(data => {
                        localStorage.setItem('token', data.access_token);
                        toast.success('Autentificare reușită!');
                        window.location.href = '/';
                    })
                    .catch(error => {
                        console.error('Facebook backend error:', error);
                        toast.error(error.message);
                        setFbLoading(false);
                    });
                } else if (response.status === 'not_authorized') {
                    toast.error('Aplicația nu este autorizată. Verifică setările Facebook App.');
                    setFbLoading(false);
                } else {
                    // User cancelled or error
                    console.log('Facebook login status:', response.status);
                    toast.info('Autentificarea cu Facebook a fost anulată');
                    setFbLoading(false);
                }
            },
            { scope: 'public_profile,email' }
        );
    };

    // 2FA Verification Screen
    if (requires2FA) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="2fa-page">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardHeader className="text-center pb-2">
                        <div className="flex justify-center mb-4">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <ShieldCheck className="w-8 h-8 text-primary" />
                            </div>
                        </div>
                        <CardTitle className="font-heading text-2xl">Verificare în doi pași</CardTitle>
                        <CardDescription>
                            Am trimis un cod de 6 cifre la<br />
                            <span className="font-medium text-foreground">{email}</span>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <form onSubmit={handleVerify2FA} className="space-y-6">
                            <div className="flex justify-center">
                                <InputOTP
                                    maxLength={6}
                                    value={otpCode}
                                    onChange={(value) => setOtpCode(value)}
                                    data-testid="otp-input"
                                >
                                    <InputOTPGroup>
                                        <InputOTPSlot index={0} className="w-12 h-14 text-xl" />
                                        <InputOTPSlot index={1} className="w-12 h-14 text-xl" />
                                        <InputOTPSlot index={2} className="w-12 h-14 text-xl" />
                                        <InputOTPSlot index={3} className="w-12 h-14 text-xl" />
                                        <InputOTPSlot index={4} className="w-12 h-14 text-xl" />
                                        <InputOTPSlot index={5} className="w-12 h-14 text-xl" />
                                    </InputOTPGroup>
                                </InputOTP>
                            </div>

                            <Button
                                type="submit"
                                className="w-full rounded-full bg-primary hover:bg-primary/90 h-11"
                                disabled={loading || otpCode.length !== 6}
                                data-testid="verify-otp-btn"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Verifică
                            </Button>
                        </form>

                        <div className="mt-6 text-center space-y-3">
                            <p className="text-sm text-muted-foreground">
                                Nu ai primit codul?{' '}
                                {resendCooldown > 0 ? (
                                    <span className="text-muted-foreground">
                                        Retrimite în {resendCooldown}s
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={handleResendOTP}
                                        className="text-primary hover:underline font-medium"
                                        data-testid="resend-otp-btn"
                                    >
                                        Retrimite codul
                                    </button>
                                )}
                            </p>
                            
                            <button
                                type="button"
                                onClick={handleBack}
                                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Înapoi la autentificare
                            </button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
            <Card className="w-full max-w-md rounded-3xl shadow-floating">
                <CardHeader className="text-center pb-2">
                    <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Gavel className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">Colectarium</span>
                    </Link>
                    <CardTitle className="font-heading text-2xl">Bine ai revenit!</CardTitle>
                    <CardDescription>Autentifică-te pentru a continua</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="email@exemplu.ro"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 rounded-xl h-11"
                                    required
                                    data-testid="login-email"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Parolă</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 pr-10 rounded-xl h-11"
                                    required
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                                Ai uitat parola?
                            </Link>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-full bg-primary hover:bg-primary/90 h-11"
                            disabled={loading}
                            data-testid="login-submit"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Autentificare
                        </Button>
                    </form>

                    <div className="relative my-6">
                        <Separator />
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-muted-foreground">
                            sau
                        </span>
                    </div>

                    <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-full h-11"
                        onClick={handleGoogleLogin}
                        data-testid="google-login"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continuă cu Google
                    </Button>

                    {process.env.REACT_APP_FACEBOOK_APP_ID && (
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full rounded-full h-11 mt-3 text-foreground"
                            onClick={handleFacebookLogin}
                            disabled={!fbLoaded || fbLoading}
                            data-testid="facebook-login"
                        >
                            {fbLoading ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                                </svg>
                            )}
                            Continuă cu Facebook
                        </Button>
                    )}

                    <p className="text-center text-sm text-muted-foreground mt-6">
                        Nu ai cont?{' '}
                        <Link to="/register" className="text-primary hover:underline font-medium">
                            Înregistrează-te
                        </Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

// Register Page - Email verification via link
export const RegisterPage = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    
    // Step: 'form' | 'email-sent'
    const [step, setStep] = useState('form');
    
    // Form data
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleSubmitForm = async (e) => {
        e.preventDefault();
        
        if (password !== confirmPassword) {
            toast.error('Parolele nu coincid');
            return;
        }

        // Frontend validation aligned with backend
        if (password.length < 8) {
            toast.error('Parola trebuie să aibă minim 8 caractere');
            return;
        }
        if (!/[A-Z]/.test(password)) {
            toast.error('Parola trebuie să conțină cel puțin o literă mare');
            return;
        }
        if (!/[a-z]/.test(password)) {
            toast.error('Parola trebuie să conțină cel puțin o literă mică');
            return;
        }
        if (!/\d/.test(password)) {
            toast.error('Parola trebuie să conțină cel puțin o cifră');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch('/api/auth/register/initiate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password, username })
            });
            
            // Clone response before reading to avoid "body already consumed" error
            const text = await response.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error('Răspuns invalid de la server');
            }
            
            if (!response.ok) {
                throw new Error(data.detail || 'Eroare la înregistrare');
            }
            
            toast.success('Link de verificare trimis pe email!');
            setStep('email-sent');
        } catch (err) {
            const errorMessage = err.message || 'Eroare la înregistrare. Încearcă din nou.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleResendLink = async () => {
        setResending(true);
        try {
            const response = await fetch('/api/auth/register/resend', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            
            const text = await response.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch {
                throw new Error('Răspuns invalid de la server');
            }
            
            if (!response.ok) {
                throw new Error(data.detail || 'Eroare la retrimitere');
            }
            
            toast.success('Link nou trimis pe email!');
        } catch (err) {
            toast.error(err.message);
        } finally {
            setResending(false);
        }
    };

    // Google OAuth
    const handleGoogleLogin = () => {
        const redirectUrl = '/';
        window.location.href = `/api/auth/google/login?redirect=${encodeURIComponent(redirectUrl)}`;
    };

    // Email Sent Step UI
    if (step === 'email-sent') {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="email-sent-page">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardHeader className="text-center pb-2">
                        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-blue-600" />
                        </div>
                        <CardTitle className="font-heading text-2xl">Verifică emailul</CardTitle>
                        <CardDescription>
                            Am trimis un link de confirmare la<br />
                            <strong className="text-foreground">{email}</strong>
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                            <p className="text-sm text-blue-800">
                                Apasă pe link-ul din email pentru a-ți activa contul.
                            </p>
                            <p className="text-xs text-blue-600 mt-2">
                                Link-ul expiră în 24 de ore.
                            </p>
                        </div>

                        <div className="mt-6 text-center space-y-2">
                            <p className="text-sm text-muted-foreground">
                                Nu ai primit emailul?
                            </p>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleResendLink}
                                disabled={resending}
                                className="text-primary"
                            >
                                {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Retrimite link-ul
                            </Button>
                        </div>

                        <div className="mt-6 pt-4 border-t">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setStep('form')}
                                className="w-full text-muted-foreground"
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Înapoi la formular
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Form Step UI
    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="register-page">
            <Card className="w-full max-w-md rounded-3xl shadow-floating">
                <CardHeader className="text-center pb-2">
                    <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Gavel className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">Colectarium</span>
                    </Link>
                    <CardTitle className="font-heading text-2xl">Creează cont</CardTitle>
                    <CardDescription>Alătură-te comunității de colecționari</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                    <Button
                        type="button"
                        variant="outline"
                        className="w-full rounded-full h-11 mb-6"
                        onClick={handleGoogleLogin}
                        data-testid="google-register"
                    >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Continuă cu Google
                    </Button>

                    <div className="relative mb-6">
                        <Separator />
                        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-3 text-sm text-muted-foreground">
                            sau cu email
                        </span>
                    </div>

                    <form onSubmit={handleSubmitForm} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="username">Nume de utilizator</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="username"
                                    type="text"
                                    placeholder="ionpopescu"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="pl-10 rounded-xl h-11"
                                    required
                                    data-testid="register-username"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="email@exemplu.ro"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 rounded-xl h-11"
                                    required
                                    data-testid="register-email"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Parolă</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 pr-10 rounded-xl h-11"
                                    required
                                    data-testid="register-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmă parola</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 rounded-xl h-11"
                                    required
                                    data-testid="register-confirm-password"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full rounded-full bg-primary hover:bg-primary/90 h-11"
                            disabled={loading}
                            data-testid="register-submit"
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                            Continuă
                        </Button>
                    </form>

                    <p className="text-center text-sm text-muted-foreground mt-6">
                        Ai deja cont?{' '}
                        <Link to="/login" className="text-primary hover:underline font-medium">
                            Autentifică-te
                        </Link>
                    </p>

                    <p className="text-center text-xs text-muted-foreground mt-4">
                        Prin crearea contului, accepți{' '}
                        <Link to="/terms" className="text-primary hover:underline">Termenii și condițiile</Link>
                        {' '}și{' '}
                        <Link to="/privacy" className="text-primary hover:underline">Politica de confidențialitate</Link>
                    </p>
                </CardContent>
            </Card>
        </div>
    );
};

// Auth Callback (for Google OAuth)
export const AuthCallback = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [processing, setProcessing] = useState(true);

    useEffect(() => {
        const processAuth = async () => {
            try {
                // Check for token in query params
                const params = new URLSearchParams(location.search);
                const token = params.get('token');
                const redirect = params.get('redirect') || '/';
                const error = params.get('error');
                
                console.log('AuthCallback: Starting', { hasToken: !!token, redirect, error });
                
                if (error) {
                    toast.error(`Autentificare eșuată: ${error}`);
                    navigate('/login', { replace: true });
                    return;
                }
                
                if (!token) {
                    console.log('AuthCallback: No token found');
                    navigate('/login', { replace: true });
                    return;
                }
                
                // Store token first
                localStorage.setItem('token', token);
                console.log('AuthCallback: Token stored');
                
                // Fetch user data with relative URL
                const response = await fetch('/api/auth/me', {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                
                console.log('AuthCallback: /api/auth/me response', response.status);
                
                if (response.ok) {
                    const userData = await response.json();
                    console.log('AuthCallback: Got user', userData.email);
                    
                    // Store user data
                    localStorage.setItem('user', JSON.stringify(userData));
                    
                    toast.success('Autentificare reușită!');
                    
                    // Force page reload to pick up new auth state
                    const destination = redirect === '/login' || redirect === '/register' ? '/' : redirect;
                    window.location.href = destination;
                } else {
                    throw new Error(`Auth failed: ${response.status}`);
                }
            } catch (err) {
                console.error('AuthCallback error:', err);
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                toast.error('Autentificare eșuată');
                navigate('/login', { replace: true });
            } finally {
                setProcessing(false);
            }
        };

        processAuth();
    }, [location, navigate]);

    if (processing) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Se procesează autentificarea...</p>
                </div>
            </div>
        );
    }

    return null;
};

// Register Verify Page - Handles email verification link click
export const RegisterVerifyPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const verifyToken = async () => {
            const params = new URLSearchParams(location.search);
            const token = params.get('token');

            if (!token) {
                setStatus('error');
                setErrorMessage('Link de verificare invalid');
                return;
            }

            try {
                const response = await fetch(`/api/auth/register/verify?token=${token}`);
                const text = await response.text();
                let data;
                try {
                    data = JSON.parse(text);
                } catch {
                    throw new Error('Răspuns invalid de la server');
                }

                if (!response.ok) {
                    throw new Error(data.detail || 'Verificare eșuată');
                }

                // Save token and user data
                localStorage.setItem('token', data.access_token);
                localStorage.setItem('user', JSON.stringify(data.user));

                setStatus('success');
                toast.success('Cont creat cu succes! Bine ai venit!');
                
                // Redirect to home after 2 seconds
                setTimeout(() => {
                    window.location.href = '/';
                }, 2000);
            } catch (err) {
                setStatus('error');
                setErrorMessage(err.message || 'Eroare la verificare');
            }
        };

        verifyToken();
    }, [location, navigate]);

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="register-verify-page">
            <Card className="w-full max-w-md rounded-3xl shadow-floating">
                <CardContent className="pt-8 pb-8">
                    {status === 'verifying' && (
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            </div>
                            <h2 className="font-heading text-xl mb-2">Se verifică emailul...</h2>
                            <p className="text-muted-foreground text-sm">Te rugăm să aștepți</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                                <ShieldCheck className="w-8 h-8 text-green-600" />
                            </div>
                            <h2 className="font-heading text-xl mb-2">Email verificat!</h2>
                            <p className="text-muted-foreground text-sm mb-4">
                                Contul tău a fost creat cu succes.<br />
                                Vei fi redirecționat automat...
                            </p>
                            <Button
                                onClick={() => window.location.href = '/'}
                                className="rounded-full"
                            >
                                Mergi la pagina principală
                            </Button>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center">
                            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                                <Mail className="w-8 h-8 text-red-600" />
                            </div>
                            <h2 className="font-heading text-xl mb-2">Verificare eșuată</h2>
                            <p className="text-muted-foreground text-sm mb-4">{errorMessage}</p>
                            <div className="space-y-2">
                                <Button
                                    onClick={() => navigate('/register')}
                                    className="w-full rounded-full"
                                >
                                    Încearcă din nou
                                </Button>
                                <Button
                                    variant="ghost"
                                    onClick={() => navigate('/login')}
                                    className="w-full"
                                >
                                    Sau autentifică-te
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};


// Forgot Password Page
export const ForgotPasswordPage = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) {
            toast.error('Introdu adresa de email');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: email.trim() })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSent(true);
                toast.success('Link de resetare trimis!');
            } else {
                toast.error(data.detail || 'Eroare la trimiterea emailului');
            }
        } catch (error) {
            toast.error('Eroare de conexiune');
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="forgot-password-sent">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardHeader className="text-center pb-2">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-green-600" />
                        </div>
                        <CardTitle className="font-heading text-2xl">Verifică emailul</CardTitle>
                        <CardDescription>
                            Dacă adresa <strong className="text-foreground">{email}</strong> există în sistem, 
                            vei primi un link de resetare a parolei.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                            <p className="text-sm text-blue-800">
                                Link-ul de resetare expiră în 1 oră.
                            </p>
                        </div>

                        <div className="mt-6 flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => setSent(false)}
                            >
                                Încearcă alt email
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={() => navigate('/login')}
                            >
                                Înapoi la login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="forgot-password-page">
            <Card className="w-full max-w-md rounded-3xl shadow-floating">
                <CardHeader className="text-center pb-2">
                    <Link to="/" className="flex items-center justify-center gap-2 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Gavel className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">Colectarium</span>
                    </Link>
                    <CardTitle className="font-heading text-2xl">Ai uitat parola?</CardTitle>
                    <CardDescription>
                        Introdu adresa de email și îți vom trimite un link pentru resetarea parolei.
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="nume@exemplu.ro"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 h-12 rounded-xl"
                                    required
                                    data-testid="forgot-email-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl"
                            disabled={loading}
                            data-testid="forgot-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    Se trimite...
                                </>
                            ) : (
                                'Trimite link de resetare'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <Link
                            to="/login"
                            className="text-sm text-primary hover:underline inline-flex items-center gap-1"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Înapoi la autentificare
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};


// Reset Password Page
export const ResetPasswordPage = () => {
    const navigate = useNavigate();
    const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
    const token = searchParams.get('token');
    
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [tokenValid, setTokenValid] = useState(false);
    const [tokenEmail, setTokenEmail] = useState('');
    const [success, setSuccess] = useState(false);

    // Validate token on mount
    React.useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setValidating(false);
                return;
            }

            try {
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/validate/${token}`);
                const data = await response.json();
                
                if (response.ok && data.valid) {
                    setTokenValid(true);
                    setTokenEmail(data.email);
                }
            } catch (error) {
                console.error('Token validation error:', error);
            } finally {
                setValidating(false);
            }
        };

        validateToken();
    }, [token]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (password.length < 8) {
            toast.error('Parola trebuie să aibă minimum 8 caractere');
            return;
        }
        
        if (password !== confirmPassword) {
            toast.error('Parolele nu coincid');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, new_password: password })
            });
            
            const data = await response.json();
            
            if (response.ok) {
                setSuccess(true);
                toast.success('Parola a fost resetată cu succes!');
            } else {
                toast.error(data.detail || 'Eroare la resetarea parolei');
            }
        } catch (error) {
            toast.error('Eroare de conexiune');
        } finally {
            setLoading(false);
        }
    };

    if (validating) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardContent className="py-12 text-center">
                        <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
                        <p className="text-muted-foreground">Se verifică link-ul...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!token || !tokenValid) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-invalid">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardHeader className="text-center pb-2">
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <Lock className="w-8 h-8 text-red-600" />
                        </div>
                        <CardTitle className="font-heading text-2xl">Link invalid sau expirat</CardTitle>
                        <CardDescription>
                            Link-ul de resetare a parolei este invalid sau a expirat. 
                            Te rugăm să soliciți un nou link.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => navigate('/forgot-password')}
                            >
                                Solicită link nou
                            </Button>
                            <Button
                                className="flex-1"
                                onClick={() => navigate('/login')}
                            >
                                Înapoi la login
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-success">
                <Card className="w-full max-w-md rounded-3xl shadow-floating">
                    <CardHeader className="text-center pb-2">
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <Check className="w-8 h-8 text-green-600" />
                        </div>
                        <CardTitle className="font-heading text-2xl">Parolă resetată!</CardTitle>
                        <CardDescription>
                            Parola ta a fost schimbată cu succes. Te poți autentifica acum cu noua parolă.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <Button
                            className="w-full h-12 rounded-xl"
                            onClick={() => navigate('/login')}
                        >
                            Autentifică-te
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="reset-password-page">
            <Card className="w-full max-w-md rounded-3xl shadow-floating">
                <CardHeader className="text-center pb-2">
                    <Link to="/" className="flex items-center justify-center gap-2 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                            <Gavel className="w-6 h-6 text-white" />
                        </div>
                        <span className="font-heading font-bold text-2xl">Colectarium</span>
                    </Link>
                    <CardTitle className="font-heading text-2xl">Resetează parola</CardTitle>
                    <CardDescription>
                        Alege o nouă parolă pentru contul <strong className="text-foreground">{tokenEmail}</strong>
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Parolă nouă</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Minimum 8 caractere"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 pr-10 h-12 rounded-xl"
                                    required
                                    data-testid="reset-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirmă parola</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="confirmPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Repetă parola"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 h-12 rounded-xl"
                                    required
                                    data-testid="reset-confirm-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-12 rounded-xl"
                            disabled={loading}
                            data-testid="reset-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                    Se procesează...
                                </>
                            ) : (
                                'Resetează parola'
                            )}
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
};

export default LoginPage;
